import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import type { ProductAction } from '@stores/product/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { BASE_IMG_URL } from '@images/imageUrls';
import TabNav from '@components/tabNav';
import ProductList from './components/productList';
import { formatNumberToWan } from '@utils/formatNumber'
import classNames from 'classnames';
import CommonRulePopup from "@components/commonRulePopup";
import { px2rem } from '@utils/px2rem';
import { copy } from '@utils/copy';
import Toast from '@components/Toast';

interface ProductListH5PageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteAction;
    user?: UserAction;
    product?: ProductAction;
    productList?: any[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    navList?: any
    navCurrentIndex?: any
    navCurrentType?: any
    navClick?: Function
    ruleValue?: any
}
interface ProductListH5State {
    loading: boolean;
    error: any
    pageLoaded: any
    scrollY: any
    rulePopupStatus: boolean
    downloadAppStatus: boolean;
    headerShowStatus: boolean;
    showFixedNav: boolean; // 是否显示悬浮导航
}
@inject('app', 'site', 'user', 'product')
@observer
class ProductListH5Page extends BasePage<ProductListH5PageProps, ProductListH5State> {
    scrollCleanup = null;
    private navBoxRef: HTMLDivElement | null = null;
    private fixedNavRef: React.RefObject<HTMLDivElement> = React.createRef();
    
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        scrollY: 0,
        rulePopupStatus: false,
        downloadAppStatus: true,
        headerShowStatus: false,
        showFixedNav: false, // 悬浮导航是否显示
    };
    
    async componentDidMount() {
        const { app, user, product, site, location } = this.props;
        const params = new URLSearchParams(location.search);
        const isPreviewFrame = params.get('previewFrame') === '1';
        
        if (site) {
            this.setState({
                downloadAppStatus: site.getAppDownloadShowStatus(),
                headerShowStatus: site.getShowHeader(),
            }, () => {
                site.setShowHeader(false);
                site.setAppDownloadShowStatus(!isPreviewFrame);
            })
        }

        document.title = '新品体验官';

        // 直接在组件内添加滚动监听
        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            this.handleScroll(scrollY);
        };
        
        window.addEventListener('scroll', handleScroll);
        
        // 保存清理函数
        this.scrollCleanup = () => {
            window.removeEventListener('scroll', handleScroll);
        };
        
        // 立即获取一次当前滚动位置
        const initialScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        this.handleScroll(initialScrollY);
        
        super.componentDidMount();
    }

    componentWillUnmount() {
        let { headerShowStatus } = this.state;
        const { site } = this.props;
        if(site) {
            site.setShowHeader(headerShowStatus);
        }
        
        // 清理滚动监听
        if (this.scrollCleanup) {
            this.scrollCleanup();
        }
        document.title = '';
    }
    
    handleScroll = (scrollY: number) => {
        const navBoxOffsetTop = this.navBoxRef?.offsetTop || 600;
        const shouldShowFixed = scrollY > navBoxOffsetTop;
        
        // 只在状态改变时更新
        if (this.state.showFixedNav !== shouldShowFixed) {
            this.setState({ 
                scrollY: scrollY,
                showFixedNav: shouldShowFixed 
            });
        }
    }

    // 产品列表
    renderProductList = () => {
        const { productList, loading, hasMore, onLoadMore, navCurrentIndex } = this.props;
        return (
            <div style={{ marginBottom: 16 }}>
                <div>
                    <ProductList
                        list={productList}
                        loading={loading}
                        hasMore={hasMore}
                        onLoadMore={onLoadMore}
                        tabIndex={navCurrentIndex}
                        emptyText={navCurrentIndex == 0 ? "暂无试用产品" : '还未报名过新品众测活动'}
                        emptyBoxStyle={{minHeight: px2rem(1000), paddingTop: px2rem(220)}}
                    />
                </div>
            </div>
        )
    }

    // 处理导航点击
    handleNavClick = (item: any, index: number) => {
        const { navClick } = this.props;
        if (navClick) {
            navClick(item, index);
        }
    }

    // 关闭弹框
    onHidePopup = () => {
        this.setState({
            rulePopupStatus: false
        })
    }

    // 点击列表页分享按钮直接复制分享文案，并在复制完成后提示结果。
    handleShareClick = (shareText: string) => {
        try {
            copy(shareText);
            Toast.success({ content: '链接复制成功，快去分享好友吧～' });
        } catch (error) {
            Toast.error({ content: '复制失败，请稍后重试' });
        }
    };

    render() {
        let { site, navList, navCurrentIndex, navClick, ruleValue } = this.props;
        let { showFixedNav, rulePopupStatus } = this.state;
        // 从 site store 获取下载入口状态，确保关闭时能自动更新
        const downloadAppStatus = site?.appDownloadShowStatus ?? false;
        
        return (
            <div className={styles.wrapper} style={{ paddingTop: downloadAppStatus ? px2rem(88) : 0 }}>
                {/* 悬浮导航 - 只在滚动超过导航位置时显示 */}
                {showFixedNav && (
                    <div 
                        className={styles.fixedNavFilterBox} 
                        ref={this.fixedNavRef}
                        style={{ top: downloadAppStatus ? px2rem(88) : 0 }}
                    >
                        <TabNav 
                            navList={navList} 
                            currentIndex={navCurrentIndex} 
                            navClick={(item: any, index: any) => this.handleNavClick(item, index)}
                        />
                    </div>
                )}
                
                <div className={styles.topContent}>
                    <img src={`${BASE_IMG_URL}/product/img2.png`} alt="" className={styles.coverImg}/>
                    <div className={styles.enterInfoBox}>
                        <div className={styles.enterItem} onClick={() => {
                            this.setState({ rulePopupStatus: true })
                        }}>
                            <img src={`${BASE_IMG_URL}/product/icon3.png`} alt="" className={styles.enterIcon}/>
                            <span className={styles.enterText}>规则</span>
                        </div>
                        {/* 分享入口：直接复制当前新品体验官列表页的分享文案。 */}
                        <div
                            className={styles.enterItem}
                            role="button"
                            tabIndex={0}
                            onClick={() => this.handleShareClick('上vivo社区报名新品体验官，抢先免费体验vivo最新产品！\r\nhttps://club.vivo.com.cn/productList\r\n复制本消息打开vivo社区App即可查看详情~')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    this.handleShareClick('上vivo社区报名新品体验官，抢先免费体验vivo最新产品！\r\nhttps://club.vivo.com.cn/productList\r\n复制本消息打开vivo社区App即可查看详情~');
                                }
                            }}
                        >
                            <img src={`${BASE_IMG_URL}/product/icon4.png`} alt="" className={styles.enterIcon}/>
                            <span className={styles.enterText}>分享</span>
                        </div>
                    </div>
                </div>
                
                <div className={styles.navBox} ref={(ref) => { this.navBoxRef = ref; }}>
                    <TabNav 
                        navList={navList} 
                        currentIndex={navCurrentIndex} 
                        navClick={(item: any, index: any) => this.handleNavClick(item, index)}
                    />
                </div>
                
                <div className={styles.productContent}>
                    {this.renderProductList()}
                </div>
                
                {/* 规则弹框 */}
                <CommonRulePopup
                    visible={rulePopupStatus}
                    title="新品体验官规则"
                    confirmText="我知道了"
                    richtextContent={ruleValue}
                    useRichtext={true}
                    onConfirmButtonChange={() => this.onHidePopup()}
                />

            </div>
        )
    }
}

export default withRouter(ProductListH5Page);
